/**
 * Cirugía oral e inclusiones dentales (CK-CIR-ORAL): planificación, lista de verificación de los tiempos quirúrgicos
 * (se guarda sola mientras se opera), firma de la cirugía en la evolución y controles.
 */
import { imprimir as imprimirHoja } from "../imprimir.ts";
import { useEffect, useRef, useState } from "react";
import { api, puede, type Paciente } from "../api.ts";
import {
  checklistCirugia, pasosOmitidosCirugia, problemasPlanCirugia, avisosPlanCirugia, problemasCirugiaOral, avisosCirugiaOral, resumenCirugiaOral, indicacionCirugia, planCirugiaVacio, cirugiaOralVacia,
  zonaCirugia, type PlanCirugia, type CirugiaOral, type PasoCheck,
} from "../../../dominio/src/index.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";
import { CampoPieza } from "../componentes/PiezaOdontograma.tsx";
import { Opciones } from "./Implantes.tsx";
import { BorrarCaso, CasosBorrados } from "../componentes/BorrarCaso.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";
import { FaltaConsentimiento, type SinConsentimiento } from "../componentes/FaltaConsentimiento.tsx";

type Cat = Record<string, string[]>;
interface Control { id: string; fecha: string; tipo: string; datos: { alertas?: string[] }; resumen: string; profesional: string }
interface Caso {
  anulado_en?: string | null; motivo_anulacion?: string; anulado_por?: string | null;
  id: string; estado: "en_curso" | "terminado" | "anulado"; plan: PlanCirugia; piezas: number[]; zona: string; cirugia: CirugiaOral | null;
  fecha_cirugia: string | null; cirugia_firmada_en: string | null; firmada_por: string | null;
  fechas: { retiroPuntos?: string; resultadoPatologia?: string; controlTraccion?: string };
  creado: string; controles: Control[]; checklist: PasoCheck[]; avisos: string[];
  /** lo que Donna descontó del inventario al firmar (solo viene en la respuesta de la firma) */
  inventario?: { descontados: { item: string; cantidad: number; disponible: number }[]; sinItem: string[] };
}
const fechaLarga = (iso?: string | null) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" }) : "—");
const numero = (s: string) => (s.trim() === "" ? null : Number(s.replace(",", ".")));
const alternar = (lista: string[], k: string) => (lista.includes(k) ? lista.filter((y) => y !== k) : [...lista, k]);
const fases = ["Antes de la cirugía", "Durante la cirugía", "Al terminar"] as const;

export function CirugiaOralModulo({ paciente }: { paciente: Paciente }) {
  const [casos, setCasos] = useState<Caso[]>([]);
  const [cat, setCat] = useState<Cat | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const a = useAccion();
  const url = `/api/pacientes/${paciente.id}/cirugias`;
  useEffect(() => {
    api<{ casos: Caso[]; catalogos: Cat }>("GET", url).then((r) => {
      setCasos(r.casos); setCat(r.catalogos);
      const enCurso = r.casos.find((x) => x.estado === "en_curso");
      if (enCurso) setAbierto(enCurso.id);
    }).catch(a.setError);
  }, [paciente.id]);
  if (!cat) return <AvisoError error={a.error} />;
  const actualizar = (x: Caso) => { setCasos(casos.some((c) => c.id === x.id) ? casos.map((c) => (c.id === x.id ? x : c)) : [x, ...casos]); if (x.estado === "anulado") setAbierto(null); };
  const caso = casos.find((c) => c.id === abierto && c.estado !== "anulado");
  return (
    <div className="dos ancho">
      <div style={{ display: "grid", gap: 16 }}>
        {caso && <CasoCirugia key={caso.id} c={caso} cat={cat} alCambiar={actualizar} />}
        {nuevo && (
          <Seccion titulo="Planificar cirugía oral">
            <FormPlan cat={cat} inicial={planCirugiaVacio()} texto="Guardar planificación" alCancelar={() => setNuevo(false)}
              alGuardar={async (plan) => { const r = await api<Caso>("POST", url, { plan }); actualizar(r); setAbierto(r.id); setNuevo(false); }} />
          </Seccion>
        )}
        {!caso && !nuevo && <Seccion titulo="Cirugía oral"><p className="muted" style={{ margin: 0 }}>Elige una cirugía o planifica una nueva.</p></Seccion>}
      </div>
      <Seccion titulo="Cirugías" acciones={puede("clinico:escribir") && <button className="btn chico pri" onClick={() => { setNuevo(true); setAbierto(null); }}>Planificar cirugía</button>}>
        {casos.length === 0 && <p className="muted" style={{ margin: 0 }}>Sin cirugías orales registradas.</p>}
        <ul className="lista">{casos.filter((c) => c.estado !== "anulado").map((c) => (
          <li key={c.id}><button onClick={() => { setAbierto(c.id); setNuevo(false); }} aria-current={abierto === c.id}>
            <b className="mono">{c.zona}</b>
            <span>{c.plan.procedimientos[0]?.toLowerCase() ?? ""}{c.plan.procedimientos.length > 1 ? ` y ${c.plan.procedimientos.length - 1} más` : ""}{c.fecha_cirugia ? ` · ${fechaLarga(c.fecha_cirugia)}` : ""}</span>
            <span className={`chip ${c.estado === "en_curso" ? "rojo" : "azul"}`} style={{ marginLeft: "auto" }}>{c.estado === "en_curso" ? "En curso" : "Terminada"}</span>
          </button></li>
        ))}</ul>
        <AvisoError error={a.error} />
        <CasosBorrados casos={casos.filter((c) => c.estado === "anulado").map((c) => ({ id: c.id, titulo: `Cirugía oral · ${c.zona}`, anulado_en: c.anulado_en ?? null, motivo_anulacion: c.motivo_anulacion ?? "", anulado_por: c.anulado_por ?? null }))} />
      </Seccion>
    </div>
  );
}

/* ---------------- planificación ---------------- */

function FormPlan({ cat, inicial, texto, alGuardar, alCancelar }: { cat: Cat; inicial: PlanCirugia; texto: string; alGuardar: (p: PlanCirugia) => Promise<void>; alCancelar: () => void }) {
  const [p, setP] = useState<PlanCirugia>(inicial);
  const [piezas, setPiezas] = useState<number[]>(inicial.piezas.length ? inicial.piezas : [0]);
  const a = useAccion();
  const plan: PlanCirugia = { ...p, piezas: piezas.filter((n) => n > 0) };
  const problemas = plan.procedimientos.length || plan.piezas.length ? problemasPlanCirugia(plan) : [];
  const avisos = avisosPlanCirugia(plan);
  const [molar, supernumerario, , , , , freno] = cat.PROCEDIMIENTOS_CIRUGIA;
  const tiene = (x: string) => p.procedimientos.includes(x);
  return (
    <div className="form-factura">
      <fieldset>
        <legend>Indicación y procedimiento (marca los que se harán)</legend>
        <Opciones varias lista={cat.PROCEDIMIENTOS_CIRUGIA} valor={p.procedimientos} alElegir={(k) => setP({ ...p, procedimientos: alternar(p.procedimientos, k) })} bloqueado={false} />
      </fieldset>
      <fieldset>
        <legend>Pieza o zona</legend>
        <div className="fila" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
          {piezas.map((n, k) => (
            <div key={k} className="fila" style={{ alignItems: "flex-end" }}>
              <CampoPieza valor={n} sugeridas={[]} etiqueta={`Pieza ${k + 1}`} alCambiar={(v) => setPiezas(piezas.map((x, j) => (j === k ? v : x)))} />
              {piezas.length > 1 && <button type="button" className="btn chico" aria-label={`Quitar la pieza ${k + 1}`} onClick={() => setPiezas(piezas.filter((_, j) => j !== k))}>✕</button>}
            </div>
          ))}
          {piezas.length < 8 && <button type="button" className="btn chico" onClick={() => setPiezas([...piezas, 0])}>+ Otra pieza</button>}
          <label className="campo" style={{ flex: "1 1 220px" }}>Zona (si no es una pieza)<input value={p.zona} maxLength={80} placeholder="Frenillo labial superior, torus palatino…" onChange={(e) => setP({ ...p, zona: e.target.value })} /></label>
        </div>
      </fieldset>
      {tiene(molar) && (
        <fieldset>
          <legend>Tercer molar</legend>
          <div className="fila" style={{ flexWrap: "wrap" }}>
            <label className="campo">Pell &amp; Gregory · clase<select value={p.claseMolar} onChange={(e) => setP({ ...p, claseMolar: e.target.value })}><option value="">—</option>{cat.CLASE_PELL_GREGORY.map((x) => <option key={x}>{x}</option>)}</select></label>
            <label className="campo">Posición<select value={p.posicionMolar} onChange={(e) => setP({ ...p, posicionMolar: e.target.value })}><option value="">—</option>{cat.POSICION_PELL_GREGORY.map((x) => <option key={x}>{x}</option>)}</select></label>
            <label className="campo">Winter<select value={p.winter} onChange={(e) => setP({ ...p, winter: e.target.value })}><option value="">—</option>{cat.CLASIFICACION_WINTER.map((x) => <option key={x}>{x}</option>)}</select></label>
          </div>
        </fieldset>
      )}
      {tiene(supernumerario) && <div className="campo">Supernumerario<Opciones lista={cat.TIPOS_SUPERNUMERARIO} valor={p.supernumerario} alElegir={(k) => setP({ ...p, supernumerario: p.supernumerario === k ? "" : k })} bloqueado={false} /></div>}
      {tiene(freno) && <div className="campo">Frenillo<Opciones lista={cat.TIPOS_FRENECTOMIA} valor={p.frenectomia} alElegir={(k) => setP({ ...p, frenectomia: p.frenectomia === k ? "" : k })} bloqueado={false} /></div>}
      <label className="campo">Notas de la planificación<textarea value={p.notas} maxLength={2000} onChange={(e) => setP({ ...p, notas: e.target.value })} /></label>
      {problemas.length > 0 && <ul className="aviso error lista-problemas">{problemas.map((x) => <li key={x}>{x}</li>)}</ul>}
      {avisos.length > 0 && <ul className="aviso info lista-problemas">{avisos.map((x) => <li key={x}>{x}</li>)}</ul>}
      <AvisoError error={a.error} />
      <div className="fila">
        <button className="btn pri" disabled={a.ocupado || problemasPlanCirugia(plan).length > 0} onClick={() => a.correr(() => alGuardar(plan))}>{texto}</button>
        <button className="btn" onClick={alCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

/* ---------------- caso ---------------- */

function CasoCirugia({ c, cat, alCambiar }: { c: Caso; cat: Cat; alCambiar: (x: Caso) => void }) {
  const [editar, setEditar] = useState(false);
  const firmada = !!c.cirugia_firmada_en;
  const abierto = c.estado === "en_curso";
  const a = useAccion();
  return (
    <Seccion titulo={`Cirugía oral · ${c.zona}`} acciones={<span className={`chip ${abierto ? "rojo" : "azul"}`}>{abierto ? "En curso" : "Terminada"}</span>}>
      {editar ? (
        <FormPlan cat={cat} inicial={c.plan} texto="Guardar cambios" alCancelar={() => setEditar(false)}
          alGuardar={async (plan) => { alCambiar(await api<Caso>("PUT", `/api/cirugias/${c.id}`, { plan })); setEditar(false); }} />
      ) : (
        <>
          <p style={{ margin: 0 }}><b>{indicacionCirugia(c.plan)}</b></p>
          {c.plan.notas && <p style={{ margin: 0, fontSize: 14 }}>{c.plan.notas}</p>}
          {!firmada && abierto && puede("clinico:escribir") && <div><button className="btn chico" onClick={() => setEditar(true)}>Editar planificación</button></div>}
        </>
      )}
      {c.avisos.length > 0 && <ul className="aviso info lista-problemas">{c.avisos.map((x) => <li key={x}>{x}</li>)}</ul>}
      {!editar && !firmada && abierto && <Cirugia c={c} cat={cat} alCambiar={alCambiar} />}
      {firmada && (
        <>
          <h3>Cirugía</h3>
          <p style={{ margin: 0 }}>{resumenCirugiaOral(c.plan, c.cirugia!)}</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Firmada por {c.firmada_por} el {fechaLarga(c.fecha_cirugia)} · lista de verificación: {c.cirugia!.checklist.length} pasos{(c.cirugia!.omitidos?.length ?? 0) > 0 && <> · <b>{c.cirugia!.omitidos!.length} sin marcar</b>: {c.cirugia!.omitidos!.join("; ")}</>}</p>
          {c.inventario && (c.inventario.descontados.length > 0 || c.inventario.sinItem.length > 0) && (
            <div className="aviso info" role="status">
              <b>Inventario:</b> Donna descontó el material usado: {c.inventario.descontados.map((x) => `${x.item} (−${x.cantidad}${x.disponible < 0 ? ", falta anotar una compra" : `, quedan ${x.disponible}`})`).join(" · ") || "ninguno"}.
              {c.inventario.sinItem.length > 0 && <> No están en el inventario: {c.inventario.sinItem.join(", ")}.</>}
            </div>
          )}
          <div className="tiles">
            <div className="tile"><span>Retiro de puntos</span><b>{fechaLarga(c.fechas.retiroPuntos)}</b><small>Fecha orientativa</small></div>
            {c.fechas.resultadoPatologia && <div className="tile"><span>Informe de patología</span><b>{fechaLarga(c.fechas.resultadoPatologia)}</b></div>}
            {c.fechas.controlTraccion && <div className="tile"><span>Control de la tracción</span><b>{fechaLarga(c.fechas.controlTraccion)}</b></div>}
          </div>
          {abierto && puede("clinico:firmar") && <NuevoControl c={c} cat={cat} alCambiar={alCambiar} />}
        </>
      )}
      {c.controles.length > 0 && <h3>Controles</h3>}
      {[...c.controles].reverse().map((k) => (
        <article key={k.id} className="nota">
          <div className="muted" style={{ fontSize: 13 }}>{fechaLarga(k.fecha)} · {k.tipo} · {k.profesional}</div>
          <p>{k.resumen}</p>
        </article>
      ))}
      {puede("clinico:escribir") && (abierto
        ? <div><button className="btn" disabled={a.ocupado} onClick={() => a.correr(async () => alCambiar(await api<Caso>("PUT", `/api/cirugias/${c.id}/estado`, { estado: "terminado" })))}>Marcar tratamiento como finalizado</button></div>
        : <div><button className="btn chico" disabled={a.ocupado} onClick={() => a.correr(async () => alCambiar(await api<Caso>("PUT", `/api/cirugias/${c.id}/estado`, { estado: "en_curso" })))}>Reabrir caso</button></div>)}
      <BorrarCaso url={`/api/cirugias/${c.id}/anular`} que={firmada ? "esta cirugía (ya firmada)" : "esta planificación"} firmado={firmada} alBorrado={(r) => alCambiar(r as Caso)} />
      <AvisoError error={a.error} />
    </Seccion>
  );
}

/** Cirugía en curso: la lista se guarda sola a medida que se marcan los pasos. */
function Cirugia({ c, cat, alCambiar }: { c: Caso; cat: Cat; alCambiar: (x: Caso) => void }) {
  const [x, setX] = useState<CirugiaOral>(() => ({ ...cirugiaOralVacia(hoy()), ...c.cirugia }));
  const [cart, setCart] = useState(() => (x.cartuchos === null ? "" : String(x.cartuchos)));
  const [guardado, setGuardado] = useState<"" | "guardando" | "guardado" | "error">("");
  const a = useAccion();
  const escribe = puede("clinico:escribir");
  const cir: CirugiaOral = { ...x, cartuchos: numero(cart) };
  const lista = checklistCirugia(c.plan);
  const requeridos = lista.filter((p) => p.requerido);
  const hechos = requeridos.filter((p) => x.checklist.includes(p.clave)).length;
  const problemas = problemasCirugiaOral(c.plan, cir);
  const omitidos = pasosOmitidosCirugia(c.plan, cir).map((t) => t.texto);
  const avisos = avisosCirugiaOral(c.plan, cir);
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) { primera.current = false; return; }
    if (!escribe) return;
    setGuardado("guardando");
    const t = setTimeout(() => { api<Caso>("PUT", `/api/cirugias/${c.id}/cirugia`, { cirugia: cir }).then(() => setGuardado("guardado")).catch(() => setGuardado("error")); }, 1200);
    return () => clearTimeout(t);
  }, [JSON.stringify(cir)]);
  const marcar = (clave: string, si: boolean) => setX({ ...x, checklist: si ? [...x.checklist, clave] : x.checklist.filter((k) => k !== clave) });
  const unica = (campo: "antisepsia" | "farmaco" | "hoja" | "colgajo" | "hilo" | "calibre" | "patron") => (k: string) => setX({ ...x, [campo]: x[campo] === k ? "" : k });
  const multiple = (campo: "bloqueos" | "fresas" | "odontoseccion" | "elevadores" | "hemostaticos" | "complicaciones") => (k: string) => setX({ ...x, [campo]: alternar(x[campo], k) });
  const ahora = () => new Date().toTimeString().slice(0, 5);
  const bloqueado = !escribe;

  // firmar la cirugía; si falta el consentimiento informado, la pantalla ofrece declarar que se firmó en papel o que fue una urgencia
  async function firmar(sinConsentimiento?: SinConsentimiento) {
    const r = await a.correr(() => api<Caso>("POST", `/api/cirugias/${c.id}/cirugia`, { cirugia: cir, ...(sinConsentimiento ? { sinConsentimiento } : {}) }));
    if (r) alCambiar(r);
  }
  return (
    <>
      {/* lo previo a la cirugía va primero, para llenarlo antes del día de operar (se guarda solo) */}
      <fieldset className="checklist">
        <legend>Antes de la cirugía</legend>
        {lista.filter((p) => p.fase === "Antes de la cirugía").map((p) => (
          <label key={p.clave}><input type="checkbox" disabled={bloqueado} checked={x.checklist.includes(p.clave)} onChange={(e) => marcar(p.clave, e.target.checked)} /> {p.texto}{!p.requerido && <span className="muted"> (si aplica)</span>}</label>
        ))}
      </fieldset>
      <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h3 style={{ margin: 0 }}>Cirugía · tiempos quirúrgicos</h3>
        <span className="muted" style={{ fontSize: 13 }}>
          <b className="mono">{hechos}/{requeridos.length}</b> pasos obligatorios · {guardado === "guardando" ? "guardando…" : guardado === "guardado" ? "guardado ✓" : guardado === "error" ? "sin guardar (revisa la conexión)" : ""}
          {" "}<button className="enlace" onClick={() => imprimirHoja()}>Imprimir lista</button>
        </span>
      </div>
      <div className="barra-uso" aria-hidden="true"><span style={{ width: `${requeridos.length ? Math.round((hechos / requeridos.length) * 100) : 0}%` }} /></div>
      <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))" }}>
        <label className="campo">Fecha<FechaInput max={hoy()} value={x.fecha} disabled={bloqueado} onChange={(e) => setX({ ...x, fecha: e.target.value })} /></label>
        <label className="campo">Hora de inicio<span className="fila" style={{ gap: 4 }}><input type="time" value={x.hora_inicio} disabled={bloqueado} onChange={(e) => setX({ ...x, hora_inicio: e.target.value })} />{escribe && !x.hora_inicio && <button type="button" className="btn chico" onClick={() => setX({ ...x, hora_inicio: ahora() })}>Ahora</button>}</span></label>
        <label className="campo">Hora de fin<span className="fila" style={{ gap: 4 }}><input type="time" value={x.hora_fin} disabled={bloqueado} onChange={(e) => setX({ ...x, hora_fin: e.target.value })} />{escribe && x.hora_inicio && !x.hora_fin && <button type="button" className="btn chico" onClick={() => setX({ ...x, hora_fin: ahora() })}>Ahora</button>}</span></label>
      </div>

      <fieldset className="checklist protocolo">
        <legend>Protocolo por tiempos (toca para elegir)</legend>
        <section className="tiempo">
          <h4><b>Tiempo 1</b> · Antisepsia y anestesia</h4>
          <div className="campo">Antisepsia<Opciones lista={cat.ANTISEPSIA} valor={x.antisepsia} alElegir={unica("antisepsia")} bloqueado={bloqueado} /></div>
          <div className="campo">Técnica anestésica<Opciones varias lista={cat.BLOQUEOS_ANESTESICOS} valor={x.bloqueos} alElegir={multiple("bloqueos")} bloqueado={bloqueado} /></div>
          <div className="campo">Anestésico<Opciones lista={cat.FARMACOS_ANESTESICOS} valor={x.farmaco} alElegir={unica("farmaco")} bloqueado={bloqueado} /></div>
          <div className="campo">Cartuchos<Opciones lista={["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]} valor={cart} alElegir={(k) => setCart(cart === k ? "" : k)} bloqueado={bloqueado} /></div>
          <label className="campo">Cartuchos usados (descuenta del inventario)
            <input className="entrada mono" type="number" min={0} max={10} step={1} inputMode="numeric" disabled={bloqueado} value={cart}
              onChange={(e) => setCart(e.target.value === "" ? "" : String(Math.max(0, Math.min(10, Math.round(Number(e.target.value))))))} aria-label="Cantidad de cartuchos de anestésico" />
          </label>
        </section>
        <section className="tiempo">
          <h4><b>Tiempo 2</b> · Incisión y colgajo</h4>
          <div className="campo">Hoja de bisturí<Opciones lista={cat.HOJAS_CIRUGIA} valor={x.hoja} alElegir={unica("hoja")} bloqueado={bloqueado} /></div>
          <div className="campo">Diseño del colgajo<Opciones lista={cat.COLGAJOS_CIRUGIA} valor={x.colgajo} alElegir={unica("colgajo")} bloqueado={bloqueado} /></div>
        </section>
        <section className="tiempo">
          <h4><b>Tiempo 3</b> · Osteotomía y odontosección</h4>
          <div className="campo">Fresas<Opciones varias lista={cat.FRESAS_CIRUGIA} valor={x.fresas} alElegir={multiple("fresas")} bloqueado={bloqueado} /></div>
          <div className="campo">Odontosección y osteotomía<Opciones varias lista={cat.ODONTOSECCION} valor={x.odontoseccion} alElegir={multiple("odontoseccion")} bloqueado={bloqueado} /></div>
        </section>
        <section className="tiempo">
          <h4><b>Tiempo 4</b> · Luxación y extracción</h4>
          <div className="campo">Elevadores<Opciones varias lista={cat.ELEVADORES} valor={x.elevadores} alElegir={multiple("elevadores")} bloqueado={bloqueado} /></div>
          <label className="campo">Fórceps usado<input value={x.forceps} maxLength={60} disabled={bloqueado} placeholder="Ej.: 18 / 17, bayoneta, cuernos de vaca" onChange={(e) => setX({ ...x, forceps: e.target.value })} /></label>
        </section>
        <section className="tiempo">
          <h4><b>Tiempo 5</b> · Hemostasia y relleno</h4>
          <div className="campo">Hemostáticos y relleno<Opciones varias lista={cat.HEMOSTATICOS} valor={x.hemostaticos} alElegir={multiple("hemostaticos")} bloqueado={bloqueado} /></div>
        </section>
        <section className="tiempo">
          <h4><b>Tiempo 6</b> · Sutura</h4>
          <div className="campo">Hilo<Opciones lista={cat.HILOS_CIRUGIA} valor={x.hilo} alElegir={unica("hilo")} bloqueado={bloqueado} /></div>
          <div className="campo">Calibre<Opciones lista={cat.CALIBRES_CIRUGIA} valor={x.calibre} alElegir={unica("calibre")} bloqueado={bloqueado} /></div>
          <div className="campo">Patrón de sutura<Opciones lista={cat.PATRONES_SUTURA} valor={x.patron} alElegir={unica("patron")} bloqueado={bloqueado} /></div>
        </section>
        <label className="campo">Instrumentista o asistente<input value={x.instrumentista} maxLength={80} disabled={bloqueado} onChange={(e) => setX({ ...x, instrumentista: e.target.value })} /></label>
      </fieldset>

      {fases.filter((f) => f !== "Antes de la cirugía").map((f) => (
        <fieldset key={f} className="checklist">
          <legend>{f}</legend>
          {lista.filter((p) => p.fase === f).map((p) => (
            <label key={p.clave}><input type="checkbox" disabled={bloqueado} checked={x.checklist.includes(p.clave)} onChange={(e) => marcar(p.clave, e.target.checked)} /> {p.texto}{!p.requerido && <span className="muted"> (si aplica)</span>}</label>
          ))}
        </fieldset>
      ))}

      <fieldset className="checklist">
        <legend>Incidencias quirúrgicas</legend>
        <div className="chipset">{cat.COMPLICACIONES_CIRUGIA.map((k) => (
          <button type="button" key={k} className="tog red" disabled={bloqueado} aria-pressed={x.complicaciones.includes(k)} onClick={() => multiple("complicaciones")(k)}>{k}</button>
        ))}</div>
      </fieldset>
      <label className="campo">Notas de tracción, biopsia u observaciones<textarea value={x.nota} disabled={bloqueado} maxLength={2000} onChange={(e) => setX({ ...x, nota: e.target.value })} /></label>
      {avisos.length > 0 && <ul className="aviso info lista-problemas">{avisos.map((k) => <li key={k}>{k}</li>)}</ul>}
      {puede("clinico:firmar") && (
        <>
          {problemas.length > 0 ? <ul className="aviso error lista-problemas">{problemas.map((k) => <li key={k}>{k}</li>)}</ul>
            : <div className="aviso ok"><b>Así quedará en la evolución:</b> {resumenCirugiaOral(c.plan, { ...cir, omitidos })}</div>}
          {omitidos.length > 0 && problemas.length === 0 && <div className="aviso info" role="status"><b>Hay {omitidos.length} {omitidos.length === 1 ? "paso" : "pasos"} de la lista sin marcar.</b> Puedes firmar igual: quedarán registrados en la cirugía y en la evolución.<ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{omitidos.map((t) => <li key={t}>{t}</li>)}</ul></div>}
          <AvisoError error={a.error} /><FaltaConsentimiento error={a.error} ocupado={a.ocupado} alFirmarSin={firmar} />
          <div className="fila"><button className="btn pri" disabled={a.ocupado || problemas.length > 0} onClick={() => firmar()}>Firmar la cirugía</button></div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Al firmar, la cirugía queda en la evolución y ya no se puede modificar.</p>
        </>
      )}
      <div className="solo-impresion lista-impresion">
        <h2>Lista de verificación · cirugía oral · {zonaCirugia(c.plan)}</h2>
        <p>Fecha: {fechaLarga(x.fecha)} · Inicio: {x.hora_inicio || "____"} · Fin: {x.hora_fin || "____"}</p>
        <p>{indicacionCirugia(c.plan)}</p>
        {fases.map((f) => (
          <div key={f}><h3>{f}</h3><ul>{lista.filter((p) => p.fase === f).map((p) => <li key={p.clave}>{x.checklist.includes(p.clave) ? "☑" : "☐"} {p.texto}</li>)}</ul></div>
        ))}
      </div>
    </>
  );
}

function NuevoControl({ c, cat, alCambiar }: { c: Caso; cat: Cat; alCambiar: (x: Caso) => void }) {
  const [f, setF] = useState({ fecha: hoy(), tipo: cat.TIPOS_CONTROL_CIRUGIA[0], complicaciones: [] as string[], nota: "" });
  const a = useAccion();
  const set = (v: Partial<typeof f>) => setF({ ...f, ...v });
  return (
    <details>
      <summary>Registrar un control</summary>
      <div className="fila" style={{ flexWrap: "wrap" }}>
        <label className="campo">Fecha<FechaInput max={hoy()} value={f.fecha} onChange={(e) => set({ fecha: e.target.value })} /></label>
        <label className="campo">Tipo<select value={f.tipo} onChange={(e) => set({ tipo: e.target.value })}>{cat.TIPOS_CONTROL_CIRUGIA.map((x) => <option key={x}>{x}</option>)}</select></label>
      </div>
      <div className="chipset" aria-label="Complicaciones">{cat.COMPLICACIONES_CONTROL.map((k) => (
        <button type="button" key={k} className="tog red" aria-pressed={f.complicaciones.includes(k)} onClick={() => set({ complicaciones: alternar(f.complicaciones, k) })}>{k}</button>
      ))}</div>
      <label className="campo">{f.tipo === "Resultado de patología" ? "Diagnóstico del informe" : "Nota"}<textarea value={f.nota} maxLength={2000} onChange={(e) => set({ nota: e.target.value })} /></label>
      <AvisoError error={a.error} />
      <button className="btn pri" disabled={a.ocupado} onClick={async () => {
        const r = await a.correr(() => api<Caso>("POST", `/api/cirugias/${c.id}/controles`, f));
        if (r) { alCambiar(r); set({ complicaciones: [], nota: "" }); }
      }}>Firmar control</button>
    </details>
  );
}
